import { useId, type ReactNode } from "react";
import { motion } from "motion/react";
import { ArrowDownRight, ArrowUpRight, Check, ClockAlert, Hourglass, type LucideIcon, Minus, Pause, TriangleAlert } from "lucide-react";
import { cn } from "../lib/cn";
import { documentLocale, microcopy } from "../lib/microcopy";
import { TONES, type Tone } from "../lib/tone";
import { AnimatedNumber } from "./motion";

// ---------------------------------------------------------------- StatCard
export interface StatCardProps {
  label: ReactNode;
  value: number;
  format?: (n: number) => string;
  unit?: ReactNode;
  target?: number;
  targetLabel?: ReactNode;
  trend?: number[];
  direction?: "up" | "down" | "flat";
  good?: boolean;
  icon?: LucideIcon;
  className?: string;
  footer?: ReactNode;
  tone?: Tone;
}

export function StatCard({ label, value, format, unit, target, targetLabel, trend, direction = "flat", good = true, icon: Icon, className, footer, tone = "gold" }: StatCardProps) {
  const DirIcon = direction === "up" ? ArrowUpRight : direction === "down" ? ArrowDownRight : Minus;
  // Percentage deltas are meaningless from a near-zero baseline; show the direction only in that case.
  const first = trend?.[0];
  const last = trend?.[trend.length - 1];
  const delta = trend && trend.length > 1 && first !== undefined && last !== undefined && Math.abs(first) >= 1 ? Math.round(((last - first) / Math.abs(first)) * 100) : null;
  return (
    <div className={cn("card-ds relative flex flex-col overflow-hidden p-6", className)}>
      {Icon ? <Icon aria-hidden strokeWidth={1.6} className={cn("mb-4 size-8 shrink-0", tone === "success" || tone === "warning" || tone === "danger" ? TONES[tone].text : "text-aegold-600")} /> : null}
      <p className="text-[15px] font-semibold leading-snug text-aeblack-800">{label}</p>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <AnimatedNumber value={value} format={format} className="font-heading text-[2.5rem] font-extrabold tabular leading-none text-aeblack-800" />
        {unit ? <span className="text-base text-muted-foreground">{unit}</span> : null}
        {trend && trend.length > 1 ? (
          <span className={cn("ms-auto inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-xs font-medium tabular", good ? "bg-success-50 text-success-700" : "bg-danger-50 text-danger-700")}>
            <DirIcon className="size-3.5 rtl:-scale-x-100" />
            {delta !== null && Math.abs(delta) <= 300 ? `${Math.abs(delta)}%` : null}
          </span>
        ) : null}
      </div>
      {target !== undefined ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {targetLabel ?? microcopy("target")}: <span className="font-medium tabular text-foreground">{format ? format(target) : target}</span>
        </p>
      ) : null}
      {trend && trend.length > 1 ? <Sparkline data={trend} className="mt-4 h-9 w-full" good={good} /> : null}
      {footer ? <div className="mt-4 border-t border-aegold-200 pt-3 text-xs leading-relaxed text-muted-foreground">{footer}</div> : null}
    </div>
  );
}

/** Horizontal stage bars — a legible, responsive alternative to a funnel chart. */
export function StageBars({ stages, className, format }: { stages: Array<{ name: ReactNode; value: number; hint?: ReactNode; color?: string }>; className?: string; format?: (n: number) => string }) {
  const max = Math.max(1, ...stages.map((s) => s.value));
  return (
    <ol className={cn("space-y-3", className)}>
      {stages.map((s, i) => (
        <li key={i} className="grid grid-cols-[minmax(0,11rem)_minmax(0,1fr)_3rem] items-center gap-4 text-sm">
          <span className="truncate text-strong" title={typeof s.name === "string" ? s.name : undefined}>{s.name}</span>
          <span className="h-2 overflow-hidden rounded-full bg-aeblack-100">
            <motion.span className={cn("block h-full rounded-full", !s.color && "bg-aegold-600")} initial={{ width: 0 }} animate={{ width: `${Math.max(2, (s.value / max) * 100)}%` }} transition={{ duration: 0.8, ease: "easeOut", delay: i * 0.05 }} style={s.color ? { background: s.color } : { opacity: 1 - i * 0.07 }} />
          </span>
          <span className="text-end tabular font-medium text-strong">{format ? format(s.value) : s.value}</span>
        </li>
      ))}
    </ol>
  );
}

/** Single stacked bar with a legend — for small categorical breakdowns (decision outcomes, statuses). */
export function SegmentBar({ segments, className, palette }: { segments: Array<{ name: ReactNode; value: number }>; className?: string; palette: readonly string[] }) {
  const total = segments.reduce((a, b) => a + b.value, 0);
  return (
    <div className={className}>
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-aeblack-100">
        {segments.map((s, i) => (s.value > 0 ? <span key={i} className="h-full" style={{ width: `${(s.value / Math.max(1, total)) * 100}%`, background: palette[i % palette.length] }} title={`${typeof s.name === "string" ? s.name : ""} ${s.value}`} /> : null))}
      </div>
      <ul className="mt-3 space-y-1.5 text-xs">
        {segments.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className="size-2.5 shrink-0 rounded-full" style={{ background: palette[i % palette.length] }} />
            <span className="min-w-0 flex-1 truncate text-strong">{s.name}</span>
            <span className="tabular text-muted-foreground">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Sparkline({ data, className, good = true }: { data: number[]; className?: string; good?: boolean }) {
  const w = 100;
  const h = 32;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((d, i) => `${(i / (data.length - 1)) * w},${h - ((d - min) / range) * (h - 4) - 2}`).join(" ");
  const color = good ? "var(--color-success-600)" : "var(--color-danger-600)";
  const fillId = `spark-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={className} preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2, ease: "easeOut" }} />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#${fillId})`} />
    </svg>
  );
}

// ---------------------------------------------------------------- SLA ring
export type SlaHealth = "onTrack" | "atRisk" | "breached" | "completed" | "paused";

export interface SlaRingProps {
  percent: number; // 0-100 elapsed
  daysRemaining: number;
  health: SlaHealth;
  label?: ReactNode;
  size?: number;
  className?: string;
}

const SLA_HEALTH: Record<SlaHealth, { icon: LucideIcon; arc: string; iconClass: string }> = {
  onTrack: { icon: Hourglass, arc: "var(--color-success-600)", iconClass: "text-success-600" },
  atRisk: { icon: TriangleAlert, arc: "var(--color-warning-500)", iconClass: "text-warning-600" },
  breached: { icon: ClockAlert, arc: "var(--color-danger-600)", iconClass: "text-danger-600" },
  completed: { icon: Check, arc: "var(--color-sand-500)", iconClass: "text-success-600" },
  paused: { icon: Pause, arc: "var(--color-sand-400)", iconClass: "text-sand-500" },
};

/** The caption under a day count, with the Arabic number agreement (1, 2, 3–10, 11 and above). */
function dayCaption(n: number, overdue: boolean): string {
  if (documentLocale() !== "ar") return microcopy(overdue ? "daysOver" : "daysLeft");
  if (overdue) return n === 1 ? "يوم تأخير" : n === 2 ? "يومان من التأخير" : n >= 3 && n <= 10 ? "أيام تأخير" : "يوماً من التأخير";
  return n === 1 ? "يوم متبقٍ" : n === 2 ? "يومان متبقيان" : n >= 3 && n <= 10 ? "أيام متبقية" : "يوماً متبقياً";
}

/**
 * Deadline indicator: a ring showing the elapsed share of the clock with a health icon in the centre,
 * and the day count set beside the ring (never inside it, so it cannot overflow at small sizes).
 */
export function SlaRing({ percent, daysRemaining, health, label, size = 64, className }: SlaRingProps) {
  const stroke = Math.max(3, Math.round(size / 12));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(100, percent));
  const { icon: Icon, arc, iconClass } = SLA_HEALTH[health];
  const iconSize = Math.min(28, Math.round(size * 0.38));
  const overdue = daysRemaining < 0;
  const showDays = health !== "completed";
  const numberClass = size >= 64 ? "text-2xl" : size >= 48 ? "text-base" : "text-sm";
  const caption = health === "paused" ? microcopy("paused") : dayCaption(Math.abs(daysRemaining), overdue);
  return (
    <div className={cn("inline-flex items-center gap-2.5", className)}>
      <div className="relative shrink-0" style={{ width: size, height: size }} aria-hidden>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--color-sand-200)" strokeWidth={stroke} fill="none" />
          <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={arc} strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (p / 100) * c }} transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }} />
        </svg>
        <Icon className={cn("absolute inset-0 m-auto", iconClass)} style={{ width: iconSize, height: iconSize }} strokeWidth={2.25} />
      </div>
      {showDays ? (
        <div className="min-w-0 text-start leading-none">
          <div className={cn("font-semibold tabular text-strong", numberClass, overdue && "text-danger-700")}>{Math.abs(daysRemaining)}</div>
          <div className={cn("mt-1 whitespace-nowrap text-[11px] text-muted-foreground", overdue && "text-danger-600")}>{caption}</div>
        </div>
      ) : null}
      {label ? <div className={cn("min-w-0 text-sm", showDays && "border-s border-hairline ps-2.5")}>{label}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Risk gauge
export function RiskGauge({ score, className, size = 180, label }: { score: number; className?: string; size?: number; label?: ReactNode }) {
  const s = Math.max(0, Math.min(100, score));
  const r = size / 2 - 12;
  const cx = size / 2;
  const cy = size / 2;
  const startAngle = -210;
  const sweep = 240;
  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };
  const arc = (from: number, to: number) => {
    const a = polar(from);
    const b = polar(to);
    const large = to - from > 180 ? 1 : 0;
    return `M ${a.x} ${a.y} A ${r} ${r} 0 ${large} 1 ${b.x} ${b.y}`;
  };
  const needleAngle = startAngle + (s / 100) * sweep;
  const tip = polar(needleAngle);
  const band = microcopy(s >= 70 ? "riskHigh" : s >= 40 ? "riskElevated" : "riskLow");
  const bandColor = s >= 70 ? "text-danger-600" : s >= 40 ? "text-warning-600" : "text-success-600";
  return (
    <div className={cn("flex flex-col items-center", className)}>
      <svg width={size} height={size * 0.78} viewBox={`0 0 ${size} ${size * 0.78}`}>
        <path d={arc(startAngle, startAngle + sweep * 0.4)} stroke="var(--color-success-500)" strokeWidth="12" fill="none" strokeLinecap="round" opacity="0.85" />
        <path d={arc(startAngle + sweep * 0.4, startAngle + sweep * 0.7)} stroke="var(--color-warning-500)" strokeWidth="12" fill="none" opacity="0.85" />
        <path d={arc(startAngle + sweep * 0.7, startAngle + sweep)} stroke="var(--color-danger-500)" strokeWidth="12" fill="none" strokeLinecap="round" opacity="0.85" />
        <motion.line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="var(--color-aeblack-700)" strokeWidth="3" strokeLinecap="round" initial={{ rotate: -((s / 100) * sweep), originX: `${cx}px`, originY: `${cy}px` }} animate={{ rotate: 0 }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }} />
        <circle cx={cx} cy={cy} r="7" fill="white" stroke="var(--color-aeblack-700)" strokeWidth="3" />
        <text x={cx} y={cy + 34} textAnchor="middle" className="fill-aeblack-900 font-heading" style={{ fontSize: 28, fontWeight: 600 }}>{s}</text>
      </svg>
      <div className={cn("-mt-2 text-sm font-semibold", bandColor)}>{band}</div>
      {label ? <div className="text-xs text-muted-foreground">{label}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Timeline
export interface TimelineItem {
  id: string;
  title: ReactNode;
  detail?: ReactNode;
  time: ReactNode;
  actor?: ReactNode;
  kind?: "human" | "agent" | "system" | "milestone";
  icon?: LucideIcon;
}

export function Timeline({ items, className }: { items: TimelineItem[]; className?: string }) {
  return (
    <ol className={cn("relative ms-3 border-s border-border/80", className)}>
      {items.map((item, i) => {
        const Icon = item.icon;
        const dot = item.kind === "agent" ? "bg-gradient-ai" : item.kind === "milestone" ? "bg-aegold-600" : item.kind === "system" ? "bg-sand-400" : "bg-camel-600";
        return (
          <motion.li key={item.id} className="relative ps-6 pb-6 last:pb-0" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
            <span className={cn("absolute -start-[7px] top-1 grid size-3.5 place-items-center rounded-full ring-4 ring-background", dot)}>
              {Icon ? <Icon className="size-2 text-white" /> : null}
            </span>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <p className="text-sm font-medium text-foreground">{item.title}</p>
              <time className="text-xs text-muted-foreground tabular">{item.time}</time>
            </div>
            {item.detail ? <p className="mt-0.5 text-sm text-muted-foreground">{item.detail}</p> : null}
            {item.actor ? <p className="mt-1 text-xs text-subtle">{item.actor}</p> : null}
          </motion.li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Stepper
export interface Step {
  key: string;
  label: ReactNode;
  description?: ReactNode;
}

/** The UAE Design System steps: numbered circles, AE Gold 600 once reached (the current one ringed), joined by a 3px rule. */
export function Stepper({ steps, current, className, onStepClick }: { steps: Step[]; current: number; className?: string; onStepClick?: (i: number) => void }) {
  return (
    <ol className={cn("flex w-full items-center gap-2", className)}>
      {steps.map((step, i) => {
        const state = i < current ? "done" : i === current ? "active" : "todo";
        return (
          <li key={step.key} className="flex flex-1 items-center gap-2 min-w-0">
            <button
              type="button"
              disabled={!onStepClick || i > current}
              onClick={() => onStepClick?.(i)}
              aria-current={state === "active" ? "step" : undefined}
              className={cn("flex items-center gap-2.5 text-start min-w-0 rounded-lg px-1 py-0.5 transition-colors", onStepClick && i <= current && "hover:bg-aegold-50")}
            >
              <motion.span
                layout
                className={cn(
                  "grid size-9 shrink-0 place-items-center rounded-full border text-sm font-bold transition-colors",
                  state === "done" && "border-aegold-600 bg-aegold-600 text-white",
                  state === "active" && "border-aegold-600 bg-aegold-600 text-white ring-4 ring-aegold-100",
                  state === "todo" && "border-slate-200 bg-white text-aeblack-900",
                )}
              >
                {state === "done" ? <Check className="size-4" strokeWidth={3} /> : i + 1}
              </motion.span>
              <span className="hidden min-w-0 sm:block">
                <span className={cn("block truncate text-sm font-medium", state === "todo" ? "text-muted-foreground" : "text-foreground")}>{step.label}</span>
                {step.description ? <span className="block truncate text-xs text-muted-foreground">{step.description}</span> : null}
              </span>
            </button>
            {i < steps.length - 1 ? <span className={cn("h-[3px] flex-1 rounded-full bg-aeblack-200 transition-colors", i < current && "bg-aegold-600")} /> : null}
          </li>
        );
      })}
    </ol>
  );
}

// ---------------------------------------------------------------- Empty state
export function EmptyState({ icon: Icon, title, description, action, className, children, compact = false }: { icon?: LucideIcon; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string; /** Extra content under the description (a list of what the page will show, a hint). */ children?: ReactNode; compact?: boolean }) {
  return (
    <div className={cn("flex flex-col items-center justify-center rounded-xl border border-dashed border-aeblack-200 bg-white px-6 text-center", compact ? "py-8" : "py-12", className)}>
      {Icon ? (
        <span className="mb-4 grid size-12 place-items-center rounded-lg bg-aegold-50 text-aegold-600">
          <Icon className="size-5" />
        </span>
      ) : null}
      <h3 className="font-heading text-base font-bold text-aeblack-900">{title}</h3>
      {description ? <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p> : null}
      {children ? <div className="mt-4 w-full max-w-lg text-start">{children}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------- Key/value list
export function KeyValue({ items, className, columns = 2 }: { items: Array<{ label: ReactNode; value: ReactNode }>; className?: string; columns?: 1 | 2 | 3 }) {
  return (
    <div className="@container min-w-0">
      <dl className={cn("grid gap-x-6 gap-y-3", columns === 1 && "grid-cols-1", columns === 2 && "grid-cols-1 @md:grid-cols-2", columns === 3 && "grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-3", className)}>
        {items.map((it, i) => (
          <div key={i} className="min-w-0" data-empty={it.value == null || it.value === "" || it.value === "—" ? "" : undefined}>
            <dt className="text-xs font-medium text-aeblack-600">{it.label}</dt>
            <dd className="mt-0.5 text-sm font-semibold text-aeblack-900 break-words">{it.value ?? "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const LATIN_RUN = /([A-Za-z][\w&'’.\-/]*(?:[ \u00a0,()]+[A-Za-z0-9][\w&'’.\-/]*\)?)*)/;

const EASTERN_DIGITS = /[\u0660-\u0669\u06F0-\u06F9]/g;
/** One digit system on screen: Arabic-Indic digits in recorded names and quotations read as the Western digits used everywhere else. */
const westernDigits = (s: string) => s.replace(EASTERN_DIGITS, (d) => String(d.charCodeAt(0) - (d.charCodeAt(0) >= 0x06f0 ? 0x06f0 : 0x0660)));

/**
 * Words a party, a registry or a source wrote (case titles, company names, headlines): English runs stay as written and are
 * marked as English, so screen readers switch voice and the bidirectional order holds inside the Arabic line.
 */
export function SourceText({ text: raw, className }: { text?: string | null; className?: string }) {
  if (!raw) return null;
  const text = westernDigits(raw);
  if (!/[A-Za-z]/.test(text)) return <span className={className}>{text}</span>;
  if (!/[\u0600-\u06FF]/.test(text)) return <bdi lang="en" className={className}>{text}</bdi>;
  const parts = text.split(LATIN_RUN);
  return <span className={className}>{parts.map((p, i) => (i % 2 ? <bdi key={i} lang="en">{p}</bdi> : p))}</span>;
}
